import type { TFunction } from 'i18next'

import { UserDisplay } from '@/components'
import { TableColumn } from '@/components/Table'
import { Alignment, FreezePosition } from '@/components/Table/tableHelper'
import { extractMentionedUserIds } from '@/features/view-tfl-uploaded/utils/mentionUtils'
import { ResponseThread, ThreadActionableType } from '@/schema'

import { formatDisplayDate } from '../../utils'

import {
  Actionable,
  CommentCell,
  CommentStatus,
  TeamworkTaskLink,
} from './components'

export type CommentAction = 'view-task' | 'view-deliverable-set'

const LEFT_ALIGNED = {
  align: Alignment.LEFT,
  alignLabel: Alignment.LEFT,
} as const

const getAssigneeId = (content: string): string | undefined => {
  const mentionedUserIds = extractMentionedUserIds(content)
  return mentionedUserIds?.[0]
}

export const getCommentListColumns = (
  _handleActionClick: (action: CommentAction, item: ResponseThread) => void,
  t: TFunction,
  _isFetching = false
): TableColumn<ResponseThread>[] => [
  {
    id: 'content',
    label: t('comment.columns.comment'),
    width: 240,
    sortable: false,
    renderCell: (value) => {
      const content = typeof value === 'string' ? value : ''
      return <CommentCell content={content} />
    },
    ...LEFT_ALIGNED,
    isFrozen: true,
    freezePosition: FreezePosition.LEFT,
    disableTruncation: true,
  },
  {
    id: 'project_name',
    label: t('comment.columns.projectName'),
    width: 200,
    sortable: true,
    renderCell: (_value, row) => {
      const name = row?.project?.project_name || ''
      return <span>{name}</span>
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'deliverable_set',
    label: t('comment.columns.deliverableSet'),
    width: 200,
    sortable: true,
    renderCell: (_value, row) => {
      const name = row.deliverable_set?.name || ''
      return <span>{name}</span>
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'file_name',
    label: t('comment.columns.fileName'),
    width: 200,
    sortable: true,
    renderCell: (value) => {
      const fileName = typeof value === 'string' ? value : ''
      return <span>{fileName}</span>
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'version_number',
    label: t('comment.columns.versionNumber'),
    width: 160,
    sortable: false,
    renderCell: (value) => {
      return (
        <span className="flex items-center h-[34px]">
          {value ? `${Number(value).toFixed(1)}` : '-'}
        </span>
      )
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'owner_full_name',
    label: t('comment.columns.owner'),
    width: 150,
    renderCell: (_value, row) => {
      return <UserDisplay user={row.owner} showAvatar={false} />
    },
    ...LEFT_ALIGNED,
    sortable: true,
  },
  {
    id: 'thread_status',
    label: t('comment.columns.status'),
    width: 120,
    sortable: true,

    renderCell: (value) => {
      const status = value as 'open' | 'resolved'
      return <CommentStatus status={status} />
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'created_at',
    label: t('document.columns.createdDate'),
    width: 150,
    sortable: true,
    ...LEFT_ALIGNED,
    renderCell: (value) => <span>{formatDisplayDate(value as string)}</span>,
  },
  {
    id: 'actionable_type',
    label: t('comment.columns.reviewStatus'),
    width: 180,
    sortable: true,
    renderCell: (_value, row) => {
      const actionableType = row.actionable_type
      const isNotReviewed = actionableType === ThreadActionableType.NONE
      const isReviewed =
        actionableType === ThreadActionableType.NOT_ACTIONABLE ||
        actionableType === ThreadActionableType.ACTIONABLE

      if (isNotReviewed) {
        return (
          <span className="text-error-primary-600">
            {t('comment.reviewStatus.notReviewed')}
          </span>
        )
      }
      if (isReviewed) {
        return (
          <span className="text-primary-600">
            {t('comment.reviewStatus.reviewed')}
          </span>
        )
      }
      return null
    },
    ...LEFT_ALIGNED,
  },
  {
    id: 'teamwork_task',
    label: t('comment.columns.teamworkTask'),
    width: 180,
    renderCell: (_value, row) => {
      const assigneeId = getAssigneeId(row.content)

      return (
        <div className="ml-2">
          <TeamworkTaskLink thread={row} assigneeId={assigneeId} />
        </div>
      )
    },
    ...LEFT_ALIGNED,
    cellClassName: 'p-0',
    disableRowClick: true,
  },
  {
    id: 'action',
    label: t('comment.columns.actionable'),
    width: 180,
    renderCell: (_value, row) => {
      const actionType = row.actionable_type as ThreadActionableType
      const assigneeId = getAssigneeId(row.content)

      return (
        <Actionable
          actionableType={actionType}
          threadId={row.thread_id}
          assigneeId={assigneeId}
        />
      )
    },
    ...LEFT_ALIGNED,
    cellClassName: 'p-0',
    disableRowClick: true,
  },
]
